import { useEffect } from "react";
import ES from "@/lib/i18n_es.json";

const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = "textarea, input, script, style, code, pre, .prose-gv, [contenteditable='true'], [data-no-translate], [data-testid='tour-panel'], [data-testid='tour-offer']";
const original = new WeakMap();
const written = new WeakMap();
const PREFIX = Object.keys(ES).filter((k) => k.endsWith("{n}") && k.indexOf("{n}") === k.length - 3).map((k) => [k.slice(0, -3), ES[k]]);
const attrOriginal = new WeakMap();

const NUM = /\d[\d,]*(?:\.\d+)?/g;
export function toEs(key) {
  if (ES[key]) return ES[key];
  const nums = key.match(NUM);
  const tpl = nums && ES[key.replace(NUM, "{n}")];
  if (!tpl) {
    const p = PREFIX.find(([pre]) => pre.length > 3 && key.startsWith(pre));
    return p ? p[1].replace("{n}", key.slice(p[0].length)) : null;
  }
  let i = 0;
  return tpl.replace(/\{n\}/g, () => nums[i++] ?? "");
}

function translateText(node) {
  const raw = node.nodeValue;
  const key = raw.trim();
  if (!key || written.get(node) === raw || node.parentElement?.closest(SKIP)) return;
  const es = toEs(key);
  if (!es || es === key) return;
  original.set(node, raw);
  const next = raw.replace(key, es);
  written.set(node, next);
  node.nodeValue = next;
}

function translateEl(el) {
  if (el.closest?.(SKIP) && el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return;
  for (const a of ATTRS) {
    const v = el.getAttribute?.(a);
    const es = v && toEs(v.trim());
    if (es && es !== v.trim() && attrOriginal.get(el)?.[`_w_${a}`] !== v) {
      const saved = attrOriginal.get(el) || {};
      saved[a] = v;
      saved[`_w_${a}`] = es;
      attrOriginal.set(el, saved);
      el.setAttribute(a, es);
    }
  }
}

function walk(root) {
  if (root.nodeType === 3) return translateText(root);
  if (root.nodeType !== 1) return;
  translateEl(root);
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = tw.nextNode(); n; n = tw.nextNode()) (n.nodeType === 3 ? translateText(n) : translateEl(n));
}

function restore(root) {
  const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let n = tw.nextNode(); n; n = tw.nextNode()) {
    if (n.nodeType === 3 && original.has(n)) { if (written.get(n) === n.nodeValue) n.nodeValue = original.get(n); original.delete(n); written.delete(n); }
    if (n.nodeType === 1 && attrOriginal.has(n)) { Object.entries(attrOriginal.get(n)).forEach(([a, v]) => !a.startsWith("_w_") && n.setAttribute(a, v)); attrOriginal.delete(n); }
  }
}

export function useSpanishScreens(active) {
  useEffect(() => {
    if (!active) return;
    document.documentElement.lang = "es";
    walk(document.body);
    const obs = new MutationObserver((muts) => {
      for (const m of muts) {
        if (m.type === "characterData") translateText(m.target);
        else if (m.type === "attributes") translateEl(m.target);
        else m.addedNodes.forEach(walk);
      }
    });
    obs.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    return () => { obs.disconnect(); restore(document.body); document.documentElement.lang = "en"; };
  }, [active]);
}
